<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="h-100">
      <ReportFilterPopover
        v-if="displayFilter"
        headerTitle="Inventory Value Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyStockLevelFilter"
        @reset="resetStockLevelFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingStockLevelFilter.startDate"
              :endDate="editingStockLevelFilter.endDate"
              @updateStartDate="editingStockLevelFilter.startDate = $event"
              @updateEndDate="editingStockLevelFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Stock Filter" v-if="stockFilterEnabled">
            <FormInputStockCode
              v-model="editingStockLevelFilter.stockId"
              @updateStockInfo="editingStockLevelFilter.stockInfo = $event"
              placeholder="Stock filter"
              :dropdownAutoWidth="true"
              :appendToBody="true"
              :stockType="InventoryStockTypes.glass" />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedStockLevelFilter.startDate | formatDate}} - {{appliedStockLevelFilter.endDate | formatDate}}
          </div>
          <div v-if="hasStockFilter">
            <i class="far fa-inventory mr-2"></i>{{appliedStockLevelFilter.stockInfo ? appliedStockLevelFilter.stockInfo.name : 'None'}}
          </div>
        </template>
      </ReportFilterPopover>

      <Spinner v-if="loading" />
      <ChartLine
        v-if="!loading"
        title="Inventory Value"
        :series="stockLevelData.series"
        :xAxis="stockLevelData.xAxis"
        :yAxis="stockLevelData.yAxis"
        :legend="stockLevelData.legend"
        :options="stockLevelData.options"
        :popupEnabled="popupEnabled"
        :exportEnabled="exportEnabled"
      />
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import ChartLine from '@/components/charts/chartLine'
import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'InventoryStockLevelChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartLine,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    FormInputStockCode
  },
  props: {
    title: String,
    subTitle: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    popupEnabled: {
      type: Boolean,
      default: true
    },
    exportEnabled: {
      type: Boolean,
      default: true
    },
    displayFilter: {
      type: Boolean,
      default: true
    },
    stockFilterEnabled: {
      type: Boolean,
      default: true
    },
    stockId: Number,
    glassSizeId: Number,
    startDate: String,
    endDate: String,
  },
  data() {
    return {
      loading: false,
      stockLevelData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultStockLevelFilter: {    
        startDate: moment().local().subtract(12, 'months').startOf('day').toDate().toISOString(),
        endDate: moment().local().add(12, 'months').endOf('day').toDate().toISOString(),
        stockId: this.stockId,
        stockInfo: {}
      },
      editingStockLevelFilter: {},
      appliedStockLevelFilter: {},
    }
  },
  computed: {
    hasStockFilter() {
      return !!this.defaultStockLevelFilter.stockId && this.stockFilterEnabled
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  mounted() {
    this.editingStockLevelFilter = _.clone(this.defaultStockLevelFilter)
    this.appliedStockLevelFilter = _.clone(this.defaultStockLevelFilter)

    if(this.startDate) {
      this.defaultStockLevelFilter.startDate = this.startDate
      this.editingStockLevelFilter.startDate = this.startDate
      this.appliedStockLevelFilter.startDate = this.startDate
    }
    if(this.endDate) {
      this.defaultStockLevelFilter.endDate = this.endDate
      this.editingStockLevelFilter.endDate = this.endDate
      this.appliedStockLevelFilter.endDate = this.endDate
    }

    this.requestChartData()
  },
  watch: {
    stockId: function() {
      this.defaultStockLevelFilter.stockId = this.stockId
      this.editingStockLevelFilter.stockId = this.stockId
      this.appliedStockLevelFilter.stockId = this.stockId

      this.requestChartData()
    },
    glassSizeId: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.defaultStockLevelFilter.startDate = this.startDate
      this.editingStockLevelFilter.startDate = this.startDate
      this.appliedStockLevelFilter.startDate = this.startDate

      this.requestChartData()
    },
    endDate: function() {
      this.defaultStockLevelFilter.endDate = this.endDate
      this.editingStockLevelFilter.endDate = this.endDate
      this.appliedStockLevelFilter.endDate = this.endDate

      this.requestChartData()
    },
  },
  methods: {
    applyStockLevelFilter() {
      this.appliedStockLevelFilter = _.clone(this.editingStockLevelFilter)

      this.requestChartData()
    },
    resetStockLevelFilter() {
      this.editingStockLevelFilter = _.clone(this.defaultStockLevelFilter)
      this.appliedStockLevelFilter = _.clone(this.defaultStockLevelFilter)

      this.requestChartData()
    },
    requestChartData() {
      var stockLevelFilter = {
        type: 'daily',
        startDate: this.appliedStockLevelFilter.startDate,
        endDate: this.appliedStockLevelFilter.endDate,
        stockId: this.appliedStockLevelFilter.stockId,
      }

      let widgetComp = this
      this.loading = true

      this.$apiService.analyticsService.getStockOnHandGlassChartData(stockLevelFilter)
      .then(response => {
        let seriesList = response.data

        var stockLevelSeriesList = seriesList.filter(seriesData => seriesData.type == 'stockLevel')

        // Filter by glass size if applicable
        if(this.glassSizeId) {
          stockLevelSeriesList = stockLevelSeriesList.filter(seriesData => seriesData.glassSizeId == this.glassSizeId)
        }

        var purchaseOrderList = seriesList.filter(seriesData => seriesData.type == 'projectedPurchaseOrder')
        var glassSizeList = _.uniqBy(stockLevelSeriesList, 'glassSizeId')

        this.stockLevelData.legend = {
          data: glassSizeList.map(stockLevelSeries => `${stockLevelSeries.height}x${stockLevelSeries.width}`)
        }

        this.stockLevelData.xAxis = {
          type: 'time',
          maxInterval: 30 * 24 * 3600 * 1000,    // 1 Month
        }
        this.stockLevelData.yAxis = {
          type: 'value',
        }

        this.stockLevelData.options.tooltip = {
          trigger: 'axis',
          axisPointer: {
            label: {
              formatter: function (params) {
                return moment(params.value).format('DD MMM YY');
              }
            }
          }
        }
        
        this.stockLevelData.series = []

        let projectionAreaAdded = false
        stockLevelSeriesList.forEach(stockLevelSeries => {
          let markPoint = {}
          let markArea = {}

          if(stockLevelSeries.isProjected) {
            purchaseOrderList.forEach(purchaseOrderSeries => {
              purchaseOrderSeries.data.filter(poInfo => poInfo.glassSizeId == stockLevelSeries.glassSizeId).forEach(poInfo => {
                
                markPoint.symbolSize = 40
                markPoint.label = {
                  show: true,
                  position: 'top',
                  formatter: '{b}',
                  fontSize: 10
                }

                // Note: PO has 2 dates from reports. expectedDate is the date it is expected to arrive & date is expectedDate or now, whatever is later
                let dayInfo = stockLevelSeries.data.find(data => moment(data.date).isSame(moment(poInfo.date), 'day'))

                markPoint.data = _.concat(markPoint.data || [], {
                  name: `PO: ${poInfo.sequenceNumber}`,
                  coord: [moment(poInfo.date).format('YYYY-MM-DD'), widgetComp.formatInteger(dayInfo ? dayInfo.totalNonOffcutSheetsOnHand : 0, false, true)],
                })

                markPoint.tooltip = {
                  trigger: 'item',
                  formatter: `Purchase Order <strong>${poInfo.sequenceNumber}</strong><br /><strong>${poInfo.totalSheets}</strong> Sheets of <strong>${stockLevelSeries.height}x${stockLevelSeries.width}</strong> Incoming on <strong>${moment(poInfo.expectedDate).format('DD MMM YYYY')}</strong>`,
                }
              })
            })
          }

          // Only show projection area on 1st series with projection
          if(stockLevelSeries.isProjected && !projectionAreaAdded) {
            markArea = {
              itemStyle: {
                color: 'rgba(200, 200, 255, 0.225)'
              },
              data: [
                [
                  { name: 'Projection', xAxis: moment(stockLevelSeries.data[0].date).format('YYYY-MM-DD') },
                  { xAxis: moment(stockLevelSeries.data[stockLevelSeries.data.length - 1].date).format('YYYY-MM-DD') }
                ]
              ]
            }
            projectionAreaAdded = true
          }

          this.stockLevelData.series.push({
            type: 'line',
            symbol: 'none',
            name: `${stockLevelSeries.height}x${stockLevelSeries.width}`,
            data: stockLevelSeries.data.map(data => {
              return [moment(data.date).format('YYYY-MM-DD'), widgetComp.formatInteger(data.totalNonOffcutSheetsOnHand, false, true)]
            }),
            lineStyle: {
              type: stockLevelSeries.isProjected ? 'dashed' : 'solid'
            },
            markPoint: markPoint,
            markArea: markArea
          })
        })
      })
      .finally(() => {
        this.loading = false
      })
    },
  }
}
</script>